import { useEffect, useState } from 'react';
import { MultiSelectFilter, type MultiSelectOption } from '../../components/ui/MultiSelectFilter';
import { SelectField } from '../../components/ui/SelectField';
import { Input } from '../../components/ui/input';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import {
  ASSET_PRESENCE_OPTIONS,
  META_BUILD_STATUS_OPTIONS,
  GOOGLE_BUILD_STATUS_OPTIONS,
  type AssetPresenceFilter,
  type BuildStatusFilter,
} from '../../lib/assetBuildFilters';
import type { Campaign } from '../../types/campaign';
import type { CurrentUser } from '../../types/user';
import {
  DEFAULT_GRID_FILTERS,
  gridFiltersAreDefault,
  NO_GROUP,
  UNASSIGNED_BUILDER,
  type GridFilters,
  type StatusFilter,
} from '../../lib/gridFilterUrl';

// P2b-3 §6.1 + round-1 polish Fix 5: filter bar shared between the
// Meta and Google grids. Search + AM + Campaign + Group + Status +
// Builder + "Assigned to me" pill toggle. State is owned by the parent
// (the grid page) so URL sync stays in one place.
//
// AM / Campaign / Group / Builder options come pre-narrowed by the
// parent (via deriveGridOptions): only entities referenced by any
// gate-passing row appear, so toggling a filter doesn't shrink its
// own option list.
//
// The search input debounces 200ms before propagating, matching the
// CampaignList pattern.

type Props = {
  filters: GridFilters;
  onChange: (next: GridFilters) => void;
  amsInGrid: CurrentUser[];
  campaignsInGrid: Campaign[];
  groupsInGrid: Array<{ id: string; name: string }>;
  hasNoGroup: boolean;
  buildersInGrid: CurrentUser[];
  hasUnassignedBuilder: boolean;
  platform: 'meta' | 'google';
};

// Round-3 polish Fix 3: stage labels track the renamed checkboxes.
// Wire values stay stable for URL persistence (live-not-postcheck,
// scheduled-not-postcheck) — only the display labels change.
const META_STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All stages' },
  { value: 'not-built', label: 'Not yet built' },
  { value: 'built-not-live', label: 'Built, not scheduled/live' },
  { value: 'live-not-postcheck', label: 'Scheduled/Live, not post-check' },
  { value: 'complete', label: 'Complete' },
];

const GOOGLE_STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All stages' },
  { value: 'not-built', label: 'Not yet built' },
  { value: 'built-not-scheduled', label: 'Built, not scheduled' },
  { value: 'scheduled-not-postcheck', label: 'Scheduled, not budget-updated' },
  { value: 'complete', label: 'Complete' },
];

export function GridFilterBar({
  filters,
  onChange,
  amsInGrid,
  campaignsInGrid,
  groupsInGrid,
  hasNoGroup,
  buildersInGrid,
  hasUnassignedBuilder,
  platform,
}: Props) {
  const [searchInput, setSearchInput] = useState(filters.search);
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);
  useEffect(() => {
    if (searchInput === filters.search) return;
    const t = window.setTimeout(() => {
      onChange({ ...filters, search: searchInput });
    }, 200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const amOptions: MultiSelectOption[] = amsInGrid.map((u) => ({
    value: u.uid,
    label: formatUserDisplayName(u),
  }));
  const campaignOptions: MultiSelectOption[] = campaignsInGrid.map((c) => ({
    value: c.id,
    label: `${c.name}${c.isAlternate ? ' (Alt)' : c.isOptOut ? ' (Opt Out)' : ''}`,
  }));
  // Group options: "(no group)" pinned to the top when any
  // Independent location exists, then the resolved group entries.
  const groupOptions: MultiSelectOption[] = [
    ...(hasNoGroup ? [{ value: NO_GROUP, label: '(no group)' }] : []),
    ...groupsInGrid.map((g) => ({ value: g.id, label: g.name })),
  ];
  // Builder options: "(unassigned)" pinned to the top when any row
  // has no builder set, then the assigned-builder list.
  const builderOptions: MultiSelectOption[] = [
    ...(hasUnassignedBuilder
      ? [{ value: UNASSIGNED_BUILDER, label: '(unassigned)' }]
      : []),
    ...buildersInGrid.map((u) => ({
      value: u.uid,
      label: formatUserDisplayName(u),
    })),
  ];

  const statusOptions =
    platform === 'meta' ? META_STATUS_OPTIONS : GOOGLE_STATUS_OPTIONS;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        type="search"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder="Search location, campaign, AM…"
        aria-label="Search rows"
        className="h-8 flex-1 min-w-[200px] max-w-sm"
      />
      <MultiSelectFilter
        label="AMs"
        options={amOptions}
        selected={filters.am}
        onChange={(am) => onChange({ ...filters, am })}
        ariaLabel="Filter by account manager"
      />
      <MultiSelectFilter
        label="Builders"
        options={builderOptions}
        selected={filters.builders}
        onChange={(builders) => onChange({ ...filters, builders })}
        ariaLabel="Filter by builder"
      />
      <button
        type="button"
        onClick={() => onChange({ ...filters, assignedToMe: !filters.assignedToMe })}
        aria-pressed={filters.assignedToMe}
        className={
          'inline-flex items-center px-3 py-1.5 rounded-md text-sm border transition-colors ' +
          (filters.assignedToMe
            ? 'bg-slate-900 text-white border-slate-900'
            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50')
        }
        title="Show only rows assigned to me as builder"
      >
        Assigned to me
      </button>
      <MultiSelectFilter
        label="Groups"
        options={groupOptions}
        selected={filters.groups}
        onChange={(groups) => onChange({ ...filters, groups })}
        ariaLabel="Filter by group"
      />
      <SelectField
        size="sm"
        aria-label="Filter by status stage"
        value={filters.status}
        onValueChange={(v) => onChange({ ...filters, status: v as StatusFilter })}
        options={statusOptions}
      />
      <MultiSelectFilter
        label="Campaigns"
        options={campaignOptions}
        selected={filters.campaignIds}
        onChange={(campaignIds) => onChange({ ...filters, campaignIds })}
        ariaLabel="Filter by Campaign"
      />
      {/* Organic Assets filters are Meta-only (Local / Organic folders
          don't apply to the Google grid). */}
      {platform === 'meta' && (
        <MultiSelectFilter
          label="Assets"
          options={ASSET_PRESENCE_OPTIONS as MultiSelectOption[]}
          selected={filters.assetPresence}
          onChange={(vals) =>
            onChange({ ...filters, assetPresence: vals as AssetPresenceFilter[] })
          }
          ariaLabel="Filter by asset folder presence"
        />
      )}
      {/* Build status renders on both role grids — Meta shows the Meta
          dimensions (Main / Local / Organic), Google shows only "Google Main
          not built". */}
      <MultiSelectFilter
        label="Build status"
        options={
          (platform === 'meta'
            ? META_BUILD_STATUS_OPTIONS
            : GOOGLE_BUILD_STATUS_OPTIONS) as MultiSelectOption[]
        }
        selected={filters.buildStatus}
        onChange={(vals) =>
          onChange({ ...filters, buildStatus: vals as BuildStatusFilter[] })
        }
        ariaLabel="Filter by build status"
      />
      {!gridFiltersAreDefault(filters) && (
        <button
          type="button"
          onClick={() => onChange(DEFAULT_GRID_FILTERS)}
          className="text-xs text-slate-500 hover:text-slate-800 underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
